import { useEffect, useRef, useState } from "react";
import { Button } from "@dayvise/react-ui";
import { useDayviseClient, useSession } from "./providers.tsx";
import { saveFileContent } from "./fileDownload.ts";
import { useScope } from "./hooks.ts";

/** Downloads one selected skill through the authenticated owner-scoped API. */
export function SkillExportButton({ skillId }: { readonly skillId: string }) {
  const client = useDayviseClient();
  const scope = useScope();
  const { session } = useSession();
  const identity = JSON.stringify([scope.workspaceId, session.account?.userId ?? null]);
  const current = useRef(identity);
  const generation = useRef(0);
  const busy = useRef<string | null>(null);
  const [state, setState] = useState<{ identity: string; busy: boolean; error: string | null }>({ identity, busy: false, error: null });

  if (current.current !== identity) {
    generation.current++;
    busy.current = null;
  }
  current.current = identity;

  useEffect(() => {
    current.current = identity;
    return () => {
      current.current = "";
      generation.current++;
      busy.current = null;
    };
  }, [identity]);

  const download = async () => {
    if (busy.current === identity) return;
    busy.current = identity;
    const epoch = generation.current;
    const active = () => current.current === identity && generation.current === epoch;
    setState({ identity, busy: true, error: null });
    try {
      const file = await client.curator.exportSkill(skillId);
      if (active()) saveFileContent(file);
    } catch (error) {
      if (active()) setState({ identity, busy: false, error: error instanceof Error ? error.message : "Skill export failed." });
      return;
    } finally {
      if (active()) busy.current = null;
    }
    if (active()) setState({ identity, busy: false, error: null });
  };

  const visible = state.identity === identity ? state : { identity, busy: false, error: null };
  return (
    <span>
      <Button size="sm" variant="secondary" disabled={visible.busy} onClick={() => void download()}>
        {visible.busy ? "Exporting…" : "Export Markdown"}
      </Button>
      {visible.error ? <span role="alert" style={{ display: "block", color: "var(--dayvise-color-danger)" }}>{visible.error}</span> : null}
    </span>
  );
}
